import { queryOptions, useQueries } from "@tanstack/react-query";

import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context";
import { getQueryParams } from "app/pages/industry-analysis/common/getQueryParams";
import { DefaultFetchService } from "app/services/fetchService";

import { DEVICE_DISTRIBUTION_ENDPOINT } from "../consts";
import type { IDeviceDistributionApiResponse } from "../types";

const combineDeviceDistributionComparisonData = (
    primaryCategory: IDeviceDistributionApiResponse | undefined,
    comparedCategory: IDeviceDistributionApiResponse | undefined,
) => {
    if (!primaryCategory || !comparedCategory) {
        return null;
    }

    const primaryDataKey = Object.keys(primaryCategory.Data)[0];
    const comparedDataKey = Object.keys(comparedCategory.Data)[0];

    return {
        [primaryDataKey]: primaryCategory.Data[primaryDataKey],
        [comparedDataKey]: comparedCategory.Data[comparedDataKey],
    };
};

const fetchService = DefaultFetchService.getInstance();

const createQuery = (comparisonCategory: ICategoryComparison) => {
    const { category } = comparisonCategory;
    const baseQueryParams = getQueryParams();
    const queryParams = {
        ...baseQueryParams,
        category: category.forApi,
        keys: category.forApi,
        categoryHash: "categoryHash" in category ? category.categoryHash : "",
        timeGranularity: "Monthly",
    };

    return queryOptions({
        queryKey: [DEVICE_DISTRIBUTION_ENDPOINT, queryParams],
        queryFn: () => fetchService.get<IDeviceDistributionApiResponse>(DEVICE_DISTRIBUTION_ENDPOINT, queryParams),
    });
};

export const useDeviceDistributionComparisonData = (categories: ICategoryComparison[]) => {
    const [primaryCategory, comparedCategory] = categories;

    return useQueries({
        queries: [createQuery(primaryCategory), createQuery(comparedCategory)],
        combine: (results) => ({
            data: combineDeviceDistributionComparisonData(results[0].data, results[1].data),
            isLoading: results.some((result) => result.isLoading),
            isError: results.some((result) => result.isError),
        }),
    });
};
